import { Button } from "$components/button/button";
import { useRxCollectionPatient } from "$global-providers/rxdb-provider/collections/patient";
import RhfDynamicFormFields from "$routes/_lib/rhf-dynamic-form-fields/rhf-dynamic-form-fields";
import ValidationContext from "$routes/_lib/validation/validation-context";
import type { InsuranceProvider } from "$routes/new/patient/step3/_lib/insurance/types";
import type { PatientDetailsQuery } from "$routes/patient/[:patientId]/_lib/__generated__/patient-queries.gql";
import type { getInitialValues } from "$routes/patient/[:patientId]/_lib/insurance-form-helpers";
import { gql } from "@apollo/client";
import {
  InsuranceFormSchema,
  insuranceProviderToValidator,
} from "@elephant-healthcare/utils-insurance";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, makeStyles } from "@material-ui/core";
import { evolve } from "ramda";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { type AnyObjectSchema, object } from "yup";
import { useUpdatePatientInsuranceDetailsMutation } from "./__generated__/insurer-section.gql";

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
}));

gql`
  mutation updatePatientInsuranceDetails(
    $input: UpdatePatientInsuranceDetailsInput!
    $canUpdateAcrossWholeTerritory: Boolean
  ) {
    updatePatientInsuranceDetails(
      input: $input
      canUpdateAcrossWholeTerritory: $canUpdateAcrossWholeTerritory
    ) {
      uuid
      insurance {
        insuranceType
        insuranceNumber
        paymentUpToDate
        expiryDate
        visitValidation {
          expiryStrategy
        }
        insuranceProvider {
          name
          visitValidation {
            expiryStrategy
          }
        }
      }
    }
  }
`;

type InsurerSectionProps = {
  insuranceProviders: InsuranceProvider[];
  insurerId: string;
  patient: PatientDetailsQuery["patientByCardId"];
  setOpen: (open: boolean) => void;
  initialValues: ReturnType<typeof getInitialValues>;
};

export const InsurerSection = ({
  insuranceProviders,
  insurerId,
  patient,
  setOpen,
  initialValues,
}: InsurerSectionProps) => {
  const classes = useStyles();
  const patientCollection = useRxCollectionPatient();

  const [updatePatient] = useUpdatePatientInsuranceDetailsMutation({
    onCompleted: async (data) => {
      if (patientCollection && data.updatePatientInsuranceDetails) {
        try {
          const existingPatient = await patientCollection
            .findOne({
              selector: { id: patient.patient.uuid },
            })
            .exec();

          if (existingPatient) {
            const updatedInsurance =
              data.updatePatientInsuranceDetails.insurance;
            await existingPatient.incrementalPatch({
              insurance: updatedInsurance
                ? {
                    type: updatedInsurance.insuranceType,
                    number: updatedInsurance.insuranceNumber || null,
                    details: null,
                  }
                : null,
            });
          }
        } catch (error) {
          // Failed to update local patient insurance cache
        }
      }
      setOpen(false);
    },
    onError: (error) => {
      // Failed to update patient insurance
    },
  });

  const insurer = insuranceProviders.find((i) => i.id === insurerId);

  // Create validation schema using the working insuranceProviderToValidator approach
  // that properly handles both formSchema and validatorConfig.errMessages
  const validationSchema: AnyObjectSchema = insurer
    ? object({
        insuranceDetails: insuranceProviderToValidator(
          InsuranceFormSchema.REGISTRATION,
          insurer,
        ),
        visitInsuranceDetails: insuranceProviderToValidator(
          InsuranceFormSchema.VISIT,
          insurer,
        ),
      })
    : object();

  const formMethods = useForm({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues.insurance,
  });

  const handleSubmit = (values) => {
    // Early return if insurer is not available (defensive programming)
    if (!insurer) {
      return;
    }

    // Transform date fields to ISO strings for the mutation
    const dateToISOString = (value) =>
      value instanceof Date ? value.toISOString() : value;

    const transformedInsuranceDetails = evolve(
      {
        expiryDate: dateToISOString,
        issuedDate: dateToISOString,
        dateIssued: dateToISOString,
      },
      values.insuranceDetails,
    );

    return updatePatient({
      variables: {
        input: {
          insurance: {
            insuranceType: insurer.insuranceType,
            insuranceNumber: transformedInsuranceDetails.insuranceNumber,
            insuranceDetails: transformedInsuranceDetails,
            visitInsuranceDetails: values.visitInsuranceDetails,
          },
          uuid: patient.patient.uuid,
        },
        canUpdateAcrossWholeTerritory: true,
      },
    });
  };

  if (!insurer) {
    return null;
  }

  return (
    <ValidationContext.Provider value={{ validationSchema }}>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <Box>
            <RhfDynamicFormFields
              parent="insuranceDetails"
              schema={insurer.registrationValidation.formSchema}
            />
            <RhfDynamicFormFields
              parent="visitInsuranceDetails"
              schema={insurer.visitValidation.formSchema}
            />
            <Box className={classes.buttonsContainer}>
              <Button type="submit" data-testid="sidebar-confirm">
                <FormattedMessage
                  id="patient.insurance.sidebar.confirm"
                  defaultMessage="Confirm"
                />
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </ValidationContext.Provider>
  );
};
